import ShowIf from 'base/util/show-if';
import Template from './templates';
import React from 'react';
import ReactDOM from 'react-dom';
import { createAutocomplete } from '@algolia/autocomplete-core';
import { parseAlgoliaHitHighlight } from '@algolia/autocomplete-preset-algolia';
import { createConnector } from 'react-instantsearch-dom';
;
const PanelPortal = ({ children }) => {
    return ReactDOM.createPortal(children, document.body);
};
const CSHighlight = ({ hit, attribute }) => {
    return (React.createElement(React.Fragment, null, parseAlgoliaHitHighlight({
        hit,
        attribute,
    }).map(({ value, isHighlighted }, index) => {
        if (isHighlighted) {
            return (React.createElement("mark", { className: 'ais-Highlight-highlighted', key: index }, value));
        }
        return React.createElement(React.Fragment, { key: index }, value);
    })));
};
const autocompleteConnector = createConnector({
    displayName: 'CSAutocompleteConnector',
    getProvidedProps(_props, searchState) {
        const query = searchState.query || '';
        return { query };
    },
    refine(_props, searchState, newState) {
        return Object.assign(Object.assign({}, searchState), { query: newState.query });
    },
});
const Autocomplete = (props) => {
    const formRef = React.useRef(null);
    const panelRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const [autocompleteState, setAutocompleteState] = React.useState({});
    const autocomplete = React.useMemo(() => createAutocomplete(Object.assign({ onStateChange({ state }) {
            setAutocompleteState(state);
        } }, props)), []);
    const prevQuery = React.useRef(props.query);
    if (prevQuery.current !== props.query) {
        if (autocompleteState.query !== props.query) {
            autocomplete.setQuery(props.query);
        }
        prevQuery.current = props.query;
    }
    const { getEnvironmentProps } = autocomplete;
    React.useEffect(() => {
        if (!(formRef.current && panelRef.current && inputRef.current)) {
            return undefined;
        }
        const { onTouchStart, onTouchMove } = getEnvironmentProps({
            formElement: formRef.current,
            panelElement: panelRef.current,
            inputElement: inputRef.current,
        });
        window.addEventListener('touchstart', onTouchStart);
        window.addEventListener('touchmove', onTouchMove);
        return () => {
            window.removeEventListener('touchstart', onTouchStart);
            window.removeEventListener('touchmove', onTouchMove);
        };
    }, [getEnvironmentProps, formRef.current, panelRef.current, inputRef.current]);
    React.useEffect(() => {
        if (!(inputRef.current && panelRef.current))
            return undefined;
        const fixPanel = () => {
            const inputBounds = inputRef.current.getBoundingClientRect();
            const scrollTop = window.scrollY ||
                window.pageYOffset ||
                window.document.documentElement.scrollTop ||
                window.document.body.scrollTop ||
                0;
            jQuery(panelRef.current).css({
                left: inputBounds.left,
                top: inputBounds.bottom + scrollTop,
                width: inputBounds.width,
            });
        };
        window.addEventListener('resize', fixPanel, { passive: true });
        fixPanel();
        return () => {
            window.removeEventListener('resize', fixPanel);
        };
    }, [inputRef.current, panelRef.current]);
    return (React.createElement("div", Object.assign({ className: 'wm-search' }, autocomplete.getRootProps({})),
        React.createElement("form", Object.assign({ ref: formRef, className: 'wm-search__form' }, autocomplete.getFormProps({ inputElement: inputRef.current })),
            React.createElement("input", Object.assign({ ref: inputRef, placeholder: props.placeholder || '', className: 'wm-search__input', value: autocompleteState.query }, autocomplete.getInputProps({ inputElement: null }))),
            React.createElement(ShowIf, { show: autocompleteState.status === 'stalled' },
                React.createElement("div", { className: "wm-search__loadingIndicator" },
                    React.createElement(Template, { template: 'loadingIndicator' }))),
            React.createElement("button", { type: 'submit', className: "wm-search__submit" },
                React.createElement(Template, { template: 'submit' }))),
        React.createElement(PanelPortal, null,
            React.createElement("div", Object.assign({ ref: panelRef, className: 'wm-searchPanel' }, autocomplete.getPanelProps({}), { style: { position: 'absolute' } }),
                React.createElement(ShowIf, { show: autocompleteState.isOpen }, autocompleteState.collections && autocompleteState.collections.map((collection, index) => {
                    const { source, items } = collection;
                    return (React.createElement("div", { key: `source-${index}`, className: 'wm-searchPanel__collection' },
                        React.createElement(ShowIf, { show: items.length > 0 },
                            React.createElement("ul", Object.assign({ className: 'wm-searchPanel__list' }, autocomplete.getListProps()), items && items.map(item => (React.createElement("li", Object.assign({ key: item.objectID, className: 'wm-searchPanel__listItem' }, autocomplete.getItemProps({ item, source })),
                                React.createElement(CSHighlight, { hit: item, attribute: 'title' }))))))));
                }))))));
};
const CSAutocomplete = autocompleteConnector(Autocomplete);
export default CSAutocomplete;
